// ============================================================
//  src/admin/portfolio/PortfolioForm.jsx
// ============================================================

import React, { useState, useEffect, useRef } from "react";
import { CATEGORIES, apiAddPortfolio, apiUpdatePortfolio } from "./portfolioData";

const EMPTY_FORM = {
  title:       "",
  category:    CATEGORIES[0],
  description: "",
  image:       null,
  imageFile:   null,
  video:       "",
  type:        "image",
  featured:    false,
};

const PortfolioForm = ({ isOpen, onClose, onSubmit, editItem }) => {
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [errors,   setErrors]   = useState({});
  const [dragging, setDragging] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [apiError, setApiError] = useState("");

  const fileInputRef = useRef(null);
  const modalRef     = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        setForm({
          title:       editItem.title       || "",
          category:    editItem.category    || CATEGORIES[0],
          description: editItem.description || "",
          image:       editItem.image       || null,
          imageFile:   null,
          video:       editItem.video       || "",
          type:        editItem.type        || "image",
          featured:    Boolean(Number(editItem.featured)),
        });
      } else {
        setForm(EMPTY_FORM);
      }
      setErrors({});
      setApiError("");
    }
  }, [editItem, isOpen]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleBackdrop = (e) => { if (e.target === modalRef.current) onClose(); };
  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const processFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) =>
      setForm((prev) => ({
        ...prev,
        image:     e.target.result,
        imageFile: file,
        type:      "image",
      }));
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => processFile(e.target.files?.[0]);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const validate = () => {
    const e = {};
    if (!form.category) e.category = "Select a category.";
if (!form.imageFile && !editItem?.image) {
      e.image = "Please select an image.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setApiError("");

    try {
      let result;

      if (editItem) {
        result = await apiUpdatePortfolio(editItem.id, {
          title:       form.title.trim(),
          category:    form.category,
          description: form.description.trim(),
          type:        form.type,
          featured:    form.featured,
          imageFile:   form.imageFile || null,
        });
      } else {
        const fd = new FormData();
        fd.append("title",       form.title.trim());
        fd.append("category",    form.category);
        fd.append("description", form.description.trim());
        fd.append("type",        form.type);
        fd.append("featured",    form.featured ? 1 : 0);
        if (form.video)     fd.append("video", form.video.trim());
        if (form.imageFile) fd.append("image", form.imageFile);
        result = await apiAddPortfolio(fd);
      }

      if (!result.success) {
        setApiError(result.message || "Save failed. Try again.");
        setSaving(false);
        return;
      }

      onSubmit();
      setForm(EMPTY_FORM);

    } catch (err) {
      console.error("Save error:", err);
      // Show the actual error from apiFetch instead of "Is XAMPP running?"
      setApiError(err.message || "Network error. Check your server and try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="pf-backdrop"
      ref={modalRef}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={editItem ? "Edit portfolio item" : "Add portfolio item"}
    >
      <div className="pf-modal">

        <div className="pf-header">
          <div>
            <p className="pf-label">{editItem ? "Edit Item" : "New Item"}</p>
            <h2 className="pf-title">{editItem ? "Update Portfolio Item" : "Add to Portfolio"}</h2>
          </div>
          <button className="pf-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6"  x2="6"  y2="18" />
              <line x1="6"  y1="6"  x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form className="pf-body" onSubmit={handleSubmit} noValidate>

          {/* Image Upload Zone */}
          <div
            className={`pf-upload${dragging ? " pf-upload--drag" : ""}${form.image ? " pf-upload--has-image" : ""}${errors.image ? " pf-upload--error" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {form.image ? (
              <>
                <img src={form.image} alt="Preview" className="pf-upload__preview" />
                <div className="pf-upload__change">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Change Image
                </div>
              </>
            ) : (
              <>
                <div className="pf-upload__icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <p className="pf-upload__text">
                  {dragging ? "Drop to upload" : "Drop image here or click to browse"}
                </p>
                <p className="pf-upload__hint">PNG, JPG, WEBP up to 500 MB</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
          {errors.image && <p className="pf-error">{errors.image}</p>}

          {/* Type toggle + Featured */}
          <div className="pf-row">
            

            <label className="pf-checkbox">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => set("featured", e.target.checked)}
              />
              <span className="pf-checkbox__box" />
              <span className="pf-checkbox__label">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ color: "var(--gold)" }}>
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                Mark as Featured
              </span>
            </label>
          </div>

          {/* Title */}
          <div className="pf-field">
            <label className="pf-field__label" htmlFor="pf-title">
              Title <span className="pf-required">*</span>
            </label>
            <input
              id="pf-title"
              className={`pf-input${errors.title ? " pf-input--error" : ""}`}
              type="text"
              placeholder="e.g. Arjun & Priya Wedding"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              maxLength={120}
            />
            {errors.title && <p className="pf-error">{errors.title}</p>}
          </div>

          {/* Category */}
          <div className="pf-field">
            <label className="pf-field__label" htmlFor="pf-cat">
              Category <span className="pf-required">*</span>
            </label>
            <div className="pf-select-wrap">
              <select
                id="pf-cat"
                className={`pf-select${errors.category ? " pf-input--error" : ""}`}
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <svg className="pf-select-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            {errors.category && <p className="pf-error">{errors.category}</p>}
          </div>

          {/* Description */}
          <div className="pf-field">
            <label className="pf-field__label" htmlFor="pf-desc">Description</label>
            <textarea
              id="pf-desc"
              className="pf-textarea"
              placeholder="Brief description of this portfolio item…"
              rows={3}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              maxLength={400}
            />
          </div>

          
          {/* API error */}
          {apiError && (
            <div style={{
              background: "#3a1a1a",
              color: "#ff8888",
              padding: "10px 14px",
              borderRadius: 4,
              fontSize: 13,
              marginBottom: 8,
              lineHeight: 1.5,
            }}>
              ✗ {apiError}
            </div>
          )}

          {/* Actions */}
          <div className="pf-actions">
            <button type="button" className="pf-btn pf-btn--ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="submit"
              className="pf-btn pf-btn--primary"
              disabled={saving}
              style={{ opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Uploading…" : editItem ? (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Save Changes
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add Item
                </>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default PortfolioForm;
